<script setup>
// core
import {useTemplateRef} from "vue";
import {useInfiniteScroll} from "@vueuse/core";
import useScrollFade from "@/components/Telephony/api/useScrollFade";

// components
import CallNumberView from "./CallNumberView.vue";
import ConnectedNumbersStatus from "./ConnectedNumbersStatus.vue";
import TariffTime from "./TariffTime.vue";

// composables
import useExtensionsLimit from "./api/useExtensionsLimit";

const {getExtensionsInfo, isUnlimited} = useExtensionsLimit()

const props = defineProps({
    data: {
        type: Array,
        required: true,
        default: () => []
    },
    // a page is on its way: the loader row sits at the foot
    loading: {
        type: Boolean,
        default: false
    }
})

const emit = defineEmits(["loadMore", "openSettingsDialog", "openSelectSipDialog"]);
const scrollComponent = useTemplateRef('scrollComponent')
const {isBottom} = useScrollFade(scrollComponent, () => props.data?.length)

useInfiniteScroll(
    scrollComponent,
    () => {
        emit("loadMore");
    },
    {
        distance: 50,
        interval: 300,
    }
);

const onSettingsClick = (item) => {
    emit('openSettingsDialog', item)
}

const onSelectSipClick = (item) => {
    emit('openSelectSipDialog', item)
}
</script>

<template>
    <table>
        <thead class="thead">
        <tr>
            <th
                v-for="(title, index) in $tm('telephony.connected_numbers.columns')"
                :key="index"
                class="th"
            >
                <span class="headed_text">{{ title }}</span>
            </th>
        </tr>
        </thead>

        <tbody class="compact-table" ref="scrollComponent">
        <tr v-for="(item, i) in props.data" :key="i">
            <td class="number">
                <CallNumberView :number="item.number"/>
            </td>
            <td>
                <div class="tariff">
                    <span class="tariff__name">{{ item.tariff ?? '-' }}</span>
                    <span class="tariff__time"><TariffTime :item="item"/></span>
                </div>
            </td>
            <td>
                <!-- !TODO: paid_until is not in the API response yet -->
                {{ item.paid_until ?? '-' }}
            </td>
            <td>
                <ConnectedNumbersStatus :status="item.status"/>
            </td>
            <td>
                {{ item.api_key }}
            </td>
            <td class="settings">
                <div class="extensions">
                    <span class="extensions__count">{{ getExtensionsInfo(item).count }}<template v-if="getExtensionsInfo(item).limit !== null">&nbsp;/&nbsp;<BIconInfinity
                            v-if="isUnlimited(item)"
                            class="extensions__infinity"
                        /><template v-else>{{ getExtensionsInfo(item).limit }}</template></template>
                    </span>
                    <div class="inside_block" @click="onSelectSipClick(item)">
                        <BIconHeadset class="icon"/>
                    </div>
                </div>
            </td>
            <td class="settings">
                <div class="inside_block" @click="onSettingsClick(item)">
                    <BIconGear class="icon"/>
                </div>
            </td>
        </tr>
        <tr style="border-bottom: none">
            <td :colspan="7">
                <slot name="information_column"/>
            </td>
        </tr>
        <tr class="scroll-fade" :class="{ 'is-bottom': isBottom || loading }" aria-hidden="true"></tr>
        </tbody>
    </table>
</template>

<style scoped lang="scss">
@use "@/styles/table.scss";
@use "@/styles/scroll-fade.scss";

tbody tr.scroll-fade {
    @include scroll-fade.scroll-fade;
}

td {
    overflow-wrap: anywhere;
}

.number :deep(.item) {
    width: 150px;
    margin: 0 auto;
    justify-content: flex-start;
    gap: 10px;
}

.extensions {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;

    &__count {
        white-space: nowrap;
        min-width: 44px;
        text-align: center;
    }

    &__infinity {
        display: inline-block;
        font-size: 1.1em;
        vertical-align: -0.15em;
    }

    .inside_block {
        margin: 0;
    }
}

.tariff {
    display: flex;
    flex-direction: column;
    gap: 2px;

    &__name {
        font-weight: 500;
        color: var(--text-second-color);

        &::first-letter {
            text-transform: uppercase;
        }
    }

    &__time {
        font-size: 0.85rem;
        color: var(--text-light-color);
    }
}
</style>
